import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@store";
import {
  getAllTeacherService,
  createTeacherService,
  updateTeacherService,
  deleteTeacherService,
  getTeacherService,
} from "@/service/teacher.service";
import { TeacherModel, TeacherQuery } from "@/types/teacher.model";
import type { AxiosError } from "axios";
import { successToast } from "@/utils/constants";

// Define the state interface
interface TeacherState {
  teachers: TeacherModel[];
  currentTeacher: TeacherModel | null;
  loading: boolean;
  actionLoading: boolean;
  error: string | null;
  totalItems: number;
  noTeachers: boolean;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
  };
}

// Initial state
const initialState: TeacherState = {
  teachers: [],
  currentTeacher: null,
  loading: false,
  actionLoading: false,
  error: null,
  totalItems: 0,
  noTeachers: true,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 10,
  },
};

// Async thunk for fetching teachers with pagination and filtering
export const fetchTeachers = createAsyncThunk(
  "teacher/fetchAll",
  async (query: TeacherQuery, { dispatch, getState }) => {
    try {
      dispatch(setLoading(true));

      const state = getState() as RootState;

      const { school_id } = state.auth.user || {};

      if (school_id) {
        query.schoolId = school_id;
      }

      const {
        data: { data, pageInfo },
      } = await getAllTeacherService(query);
      dispatch(setLoading(false));

      dispatch(setNoTeachers(false));

      return {
        teachers: data,
        // Handle different API response formats for pagination
        pagination: {
          totalItems: pageInfo?.total_count as number,
          totalPages: pageInfo?.total_pages as number,
          currentPage: pageInfo?.current_page as number,
          pageSize: pageInfo?.per_page as number,
        },
      };
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch(setNoTeachers(true));
        throw error;
      }

      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to fetch teachers";
      dispatch(setError(message));
      throw error;
    }
  }
);

// Async thunk for fetching a single teacher by ID
export const fetchTeacherById = createAsyncThunk(
  "teacher/fetchById",
  async (id: string, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await getTeacherService(id);
      dispatch(setLoading(false));
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to fetch teacher";
      dispatch(setError(message));
      throw error;
    }
  }
);

// Async thunk for creating a new teacher
export const createNewTeacher = createAsyncThunk(
  "teacher/create",
  async (teacherData: Partial<TeacherModel>, { dispatch }) => {
    try {
      dispatch(setActionLoading(true));
      const response = await createTeacherService(teacherData as TeacherModel);
      dispatch(setActionLoading(false));
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to create teacher";
      dispatch(setError(message));
      throw error;
    }
  }
);

// Async thunk for updating an existing teacher
export const updateExistingTeacher = createAsyncThunk(
  "teacher/update",
  async (
    { id, teacherData }: { id: string; teacherData: Partial<TeacherModel> },
    { dispatch }
  ) => {
    try {
      dispatch(setActionLoading(true));
      // Ensure the teacher object has the id property
      const teacherToUpdate = { ...teacherData };
      const response = await updateTeacherService(
        id,
        teacherToUpdate as TeacherModel
      );
      dispatch(setActionLoading(false));
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to update teacher";
      dispatch(setError(message));
      throw error;
    }
  }
);

// Async thunk for deleting a teacher
export const deleteExistingTeacher = createAsyncThunk(
  "teacher/delete",
  async (id: string, { dispatch, getState }) => {
    try {
      dispatch(setActionLoading(true));
      const {
        data: { message },
      } = await deleteTeacherService(id);

      successToast(message || "Teacher deleted successfully", "");

      // Refresh the projects list after adding
      const state = getState() as RootState;
      dispatch(
        fetchTeachers({
          page: state.teacher.pagination.currentPage,
          pageSize: state.teacher.pagination.pageSize,
        })
      );

      dispatch(setActionLoading(false));
      return id;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to delete teacher";
      dispatch(setError(message));
      throw error;
    }
  }
);

// Create the slice
const teacherSlice = createSlice({
  name: "teacher",
  initialState,
  reducers: {
    setNoTeachers: (state, action: PayloadAction<boolean>) => {
      state.noTeachers = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearCurrentTeacher: (state) => {
      state.currentTeacher = null;
    },
    setActionLoading: (state, action: PayloadAction<boolean>) => {
      state.actionLoading = action.payload;
    },
    resetAllState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch teachers
      .addCase(fetchTeachers.fulfilled, (state, action) => {
        state.teachers = action.payload.teachers;
        state.pagination = action.payload.pagination;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchTeachers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch teachers";
      })

      // Fetch teacher by id
      .addCase(fetchTeacherById.fulfilled, (state, action) => {
        state.currentTeacher = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchTeacherById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch teacher";
      })

      // Create teacher
      .addCase(createNewTeacher.fulfilled, (state) => {
        state.loading = false;
        state.actionLoading = false;
        state.error = null;
      })
      .addCase(createNewTeacher.rejected, (state, action) => {
        state.loading = false;
        state.actionLoading = false;
        state.error = action.error.message || "Failed to create teacher";
      })

      // Update teacher
      .addCase(updateExistingTeacher.fulfilled, (state) => {
        state.loading = false;
        state.actionLoading = false;
        state.error = null;
      })
      .addCase(updateExistingTeacher.rejected, (state, action) => {
        state.loading = false;
        state.actionLoading = false;
        state.error = action.error.message || "Failed to update teacher";
      })

      // Delete teacher
      .addCase(deleteExistingTeacher.fulfilled, (state) => {
        state.loading = false;
        state.actionLoading = false;
        state.error = null;
      })
      .addCase(deleteExistingTeacher.rejected, (state, action) => {
        state.loading = false;
        state.actionLoading = false;
        state.error = action.error.message || "Failed to delete teacher";
      });
  },
});

// Export actions
export const {
  setNoTeachers,
  setLoading,
  setError,
  clearCurrentTeacher,
  setActionLoading,
  resetAllState,
} = teacherSlice.actions;

// Export reducer
export default teacherSlice.reducer;

// Selectors
export const selectTeachers = (state: RootState) => state.teacher.teachers;
export const selectCurrentTeacher = (state: RootState) =>
  state.teacher.currentTeacher;
export const selectTeacherLoading = (state: RootState) => state.teacher.loading;
export const selectTeacherError = (state: RootState) => state.teacher.error;
export const selectTotalItems = (state: RootState) =>
  state.teacher.pagination.totalItems;
export const selectTeacherActionLoading = (state: RootState) =>
  state.teacher.actionLoading;
export const selectNoTeachers = (state: RootState) => state.teacher.noTeachers;
